/**
 * PayNowDrawer — slide-out panel for paying orders with "Awaiting Payment" status.
 *
 * Fetches unpaid orders via ListOrdersForPayment, lets the user select/deselect
 * which orders to include, then submits a Braintree payment for the selected batch.
 * Mounted conditionally (only when the drawer is open) to avoid running hooks when hidden.
 *
 * Used by both OrdersView and OrdersViewV2 — opened via the PayNowButton.
 */
import { Button, tokens } from "@blurb/codex-react";
import { Checkbox, createStyles } from "@mantine/core";
import { GraphQL, isGraphQLError } from "@rpi/openapi-api";
import { RpiAlert, useToasts } from "@rpi/openapi-core";
import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { RpaDrawer } from "../../rpi-core/drawer/RpaDrawer";
import { useAuthUser } from "../../hooks/useAuthUser";
import { Body, Heading } from "../../components/Typography";
import { formatCount, formatCurrencyV2 } from "../../utils/misc.util";

const useStyles = createStyles({
  orderList: {
    display: "flex",
    flexDirection: "column",
    padding: `0 ${tokens.spacing[6]}`,
  },
  orderRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: tokens.spacing[3],
    padding: `${tokens.spacing[3]} 0`,
    borderBottom: `1px solid ${tokens.color.semantic.border.faint}`,
    "& .mantine-Checkbox-root": {
      marginTop: 2,
    },
  },
  orderInfo: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[1],
    flex: 1,
    minWidth: 0,
  },
  orderHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: tokens.spacing[3],
  },
  orderId: {
    wordBreak: "break-all" as const,
  },
  orderPrice: {
    flexShrink: 0,
  },
  summary: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[2],
    padding: `${tokens.spacing[4]} ${tokens.spacing[6]}`,
    borderTop: `1px solid ${tokens.color.semantic.border.faint}`,
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: tokens.spacing[3],
    padding: tokens.spacing[6],
  },
  cancelLink: {
    background: "none",
    border: "none",
    cursor: "pointer",
    textDecoration: "underline",
    color: tokens.color.semantic.text.bold,
    padding: 0,
    alignSelf: "center",
  },
  selectAll: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacing[3],
    padding: `0 ${tokens.spacing[6]} ${tokens.spacing[3]}`,
    borderBottom: `1px solid ${tokens.color.semantic.border.faint}`,
  },
  emptyState: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: tokens.spacing[10],
  },
  subtleText: {
    color: tokens.color.semantic.text.subtle,
  },
});

interface PayNowDrawerProps {
  opened: boolean;
  onClose: () => void;
}

/** Raw order type from the query — customerOrderId may be null. */
type OrderForPaymentRaw = NonNullable<NonNullable<GraphQL.ListOrdersForPaymentQuery["listOrdersForPayment"]>[number]>;
/** Narrowed type that guarantees a non-null customerOrderId (used as the selection key). */
type OrderForPayment = OrderForPaymentRaw & { customerOrderId: string };

const isValidOrder = (o: OrderForPaymentRaw | null | undefined): o is OrderForPayment =>
  o != null && o.customerOrderId != null;

export const PayNowDrawer: React.FC<PayNowDrawerProps> = ({ opened, onClose }) => {
  const { classes } = useStyles();
  const user = useAuthUser();
  const { showSuccessToast, showErrorToast } = useToasts();
  const queryClient = useQueryClient();

  const [selectedOrderIds, setSelectedOrderIds] = React.useState<Set<string>>(new Set());
  const [showSelectError, setShowSelectError] = React.useState(false);

  const {
    data: paymentMethodsData,
    isLoading: paymentMethodsLoading,
    error: paymentMethodsError,
  } = GraphQL.useListPaymentMethodsQuery({ customerPublicId: user.id! }, { enabled: opened && !!user.id });

  const paymentMethods = paymentMethodsData?.listPaymentMethods;
  const defaultPaymentMethod =
    paymentMethods && paymentMethods.length > 0
      ? paymentMethods.find((pm) => pm.isDefault) ?? paymentMethods[0]
      : undefined;

  const {
    data: ordersData,
    isLoading: ordersLoading,
    error: ordersError,
  } = GraphQL.useListOrdersForPaymentQuery(
    { customerPublicId: user.id! },
    {
      enabled: opened && !!user.id,
      onSuccess: ({ listOrdersForPayment }) => {
        const ids = new Set((listOrdersForPayment || []).filter(isValidOrder).map((o) => o.customerOrderId));
        setSelectedOrderIds(ids);
      },
    }
  );

  /** Sorted newest-first since the backend returns default DB row order (oldest-first by PK). */
  const orders = React.useMemo(
    () =>
      (ordersData?.listOrdersForPayment || [])
        .filter(isValidOrder)
        .sort((a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()),
    [ordersData]
  );

  const selectedOrders = orders.filter((o) => selectedOrderIds.has(o.customerOrderId));

  const total = {
    value: selectedOrders.reduce((sum, o) => sum + (o.pricing?.orderTotal || 0), 0),
    currency: selectedOrders[0]?.pricing?.currency ?? GraphQL.SupportedCurrency.Usd,
  };

  const allSelected = orders.length > 0 && selectedOrderIds.size === orders.length;
  const someSelected = selectedOrderIds.size > 0 && selectedOrderIds.size < orders.length;

  const toggleOrder = (customerOrderId: string) => {
    setShowSelectError(false);
    setSelectedOrderIds((prev) => {
      const next = new Set(prev);
      if (next.has(customerOrderId)) {
        next.delete(customerOrderId);
      } else {
        next.add(customerOrderId);
      }
      return next;
    });
  };

  const toggleAll = () => {
    setShowSelectError(false);
    if (allSelected) {
      setSelectedOrderIds(new Set());
    } else {
      setSelectedOrderIds(new Set(orders.map((o) => o.customerOrderId)));
    }
  };

  const { mutate: createPayment, isLoading: isPaymentLoading } = GraphQL.useCreateBraintreePaymentMutation({
    onSuccess: () => {
      showSuccessToast(`Payment for ${formatCount(selectedOrders.length, "order")} was successful.`);
      queryClient.invalidateQueries({ queryKey: ["Orders"] });
      queryClient.invalidateQueries({ queryKey: ["ListOrdersForPayment"] });
      onClose();
    },
    onError: (error) => {
      if (isGraphQLError(error)) {
        showErrorToast(error.message);
      } else {
        showErrorToast("Payment failed. Please try again.");
      }
      queryClient.invalidateQueries({ queryKey: ["Orders"] });
      queryClient.invalidateQueries({ queryKey: ["ListOrdersForPayment"] });
    },
  });

  const handlePay = () => {
    if (selectedOrders.length === 0) {
      setShowSelectError(true);
      return;
    }

    createPayment({
      input: {
        customerOrderIds: selectedOrders.map((o) => o.customerOrderId),
        token: defaultPaymentMethod?.token,
      },
    });
  };

  /** Prevent closing mid-payment to avoid orphaned transactions. */
  const handleClose = () => {
    if (!isPaymentLoading) {
      onClose();
    }
  };

  return (
    <RpaDrawer
      title="Pay Now"
      opened={opened}
      onClose={handleClose}
      isLoading={ordersLoading || paymentMethodsLoading}
      error={ordersError || paymentMethodsError}
    >
      <RpaDrawer.HeaderContent>
        <Body size="base" className={classes.subtleText}>
          Orders processed after 5:30PM EST will be processed the next business day.
        </Body>
      </RpaDrawer.HeaderContent>
      {orders.length === 0 && !ordersLoading ? (
        <div className={classes.emptyState}>
          <Body size="sm" className={classes.subtleText}>
            There are no orders awaiting payment.
          </Body>
        </div>
      ) : (
        <>
          <div className={classes.selectAll}>
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={toggleAll}
              disabled={isPaymentLoading}
            />
            <Body size="base" weight="semibold">
              {selectedOrderIds.size} of {formatCount(orders.length, "order")} selected
            </Body>
          </div>

          <div className={classes.orderList}>
            {orders.map((order) => (
              <div key={order.customerOrderId} className={classes.orderRow}>
                <Checkbox
                  checked={selectedOrderIds.has(order.customerOrderId)}
                  onChange={() => toggleOrder(order.customerOrderId)}
                  disabled={isPaymentLoading}
                />
                <div className={classes.orderInfo}>
                  <div className={classes.orderHeader}>
                    <Body size="sm" weight="semibold" className={classes.orderId}>
                      {order.customerOrderId}
                    </Body>
                    <Body size="sm" weight="semibold" className={classes.orderPrice}>
                      {formatCurrencyV2(order.pricing?.currency ?? undefined, order.pricing?.orderTotal || 0)}
                    </Body>
                  </div>
                  <Body size="xs" className={classes.subtleText}>
                    Items: {formatCurrencyV2(order.pricing?.currency ?? undefined, order.pricing?.itemsTotal || 0)}
                  </Body>
                  <Body size="xs" className={classes.subtleText}>
                    Shipping:{" "}
                    {formatCurrencyV2(order.pricing?.currency ?? undefined, order.pricing?.shippingTotal || 0)}
                  </Body>
                  <Body size="xs" className={classes.subtleText}>
                    Tax: {formatCurrencyV2(order.pricing?.currency ?? undefined, order.pricing?.taxesTotal || 0)}
                  </Body>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {!ordersLoading && !paymentMethodsLoading && !ordersError && !paymentMethodsError && (
        <RpaDrawer.Footer>
          <div className={classes.footer}>
            {defaultPaymentMethod ? (
              <div className={classes.summaryRow}>
                <Body size="sm" className={classes.subtleText}>
                  Paying with {defaultPaymentMethod.name} ending in {defaultPaymentMethod.last4Digits}
                </Body>
              </div>
            ) : (
              <RpiAlert>No payment method on file. Please add one in Billing settings.</RpiAlert>
            )}
            <div className={classes.summaryRow}>
              <Heading>Total:</Heading>
              <Heading>{formatCurrencyV2(total.currency, total.value)}</Heading>
            </div>
            {showSelectError && <RpiAlert>Please select an order</RpiAlert>}
            <Button
              variant="filled"
              intent="primary"
              size="large"
              loading={isPaymentLoading}
              disabled={!defaultPaymentMethod}
              onClick={handlePay}
            >
              {selectedOrders.length > 0 ? `Pay ${formatCount(selectedOrders.length, "Order")}` : "Pay Order"}
            </Button>
            <button className={classes.cancelLink} onClick={handleClose} disabled={isPaymentLoading}>
              <Body size="sm">Cancel</Body>
            </button>
          </div>
        </RpaDrawer.Footer>
      )}
    </RpaDrawer>
  );
};
